/**
 * Account · Security (immersive account view, tab 2 of 5).
 *
 * Password change, two-factor authentication, and active sessions — given room
 * to breathe in the shared immersive account shell. Matches ProfileTab's
 * AcctPageHead + AcctCard / AcctField / AcctButton rhythm so all five tabs feel
 * like one surface.
 *
 * Password change is REAL: it reuses the exact logic from the live
 * AccountSettings/PasswordSection (supabase.auth.updateUser + activity logging),
 * re-skinned only. Two-factor status and the active-session list are stubbed to
 * a forward-compatible shape until the backend lands; revoke /
 * sign-out-everywhere are stub handlers with honest toasts. No business logic is
 * re-implemented.
 */
import { useState } from "react";
import { Loader2, AlertTriangle, Globe, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import {
  ACTIVITY_ACTIONS,
  ACTIVITY_CATEGORIES,
  ACTIVITY_TYPES,
  activityService,
  RESOURCE_TYPES,
} from "@/services/analytics/ActivityService";
import {
  AcctButton,
  AcctCard,
  AcctPageHead,
  ACCT_BRAND as B,
} from "@/components/voice-account/VoiceAccountDetail";

const INPUT_CLASS =
  "h-[46px] w-full px-3.5 rounded-xl border border-genie-border bg-white text-[14.5px] text-[#222B45] focus:outline-none focus:border-genie-primary focus:ring-2 focus:ring-genie-primary/15 transition-all";

const FIELD_LABEL = "text-[12.5px] font-semibold text-[#64748B]";

/* ── Forward-compatible session shape (stub until BE lands) ── */
interface SecuritySession {
  id: string;
  device: string;
  where: string;
  when: string;
  current: boolean;
  tint: string;
}

const STUB_SESSIONS: SecuritySession[] = [
  {
    id: "this-device",
    device: "This device",
    where: "Current browser",
    when: "Active now",
    current: true,
    tint: B.green,
  },
  {
    id: "session-2",
    device: "iPhone · Safari",
    where: "Auckland, NZ",
    when: "2 hours ago",
    current: false,
    tint: B.blue,
  },
  {
    id: "session-3",
    device: "Windows · Edge",
    where: "Sydney, AU",
    when: "Yesterday",
    current: false,
    tint: B.amber,
  },
];

const SecurityTab = () => {
  const { user } = useAuth();

  /* ── change password (re-skin of PasswordSection — real Supabase auth) ── */
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  /* ── 2FA + sessions (stubbed to contract until BE lands) ── */
  const [sessions, setSessions] = useState<SecuritySession[]>(STUB_SESSIONS);

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPassword) {
      toast.error("Please enter a new password.");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long.");
      return;
    }

    setSavingPassword(true);
    const loadingToast = toast.loading("Updating your password...");
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;

      await activityService.logActivity({
        activityType: ACTIVITY_TYPES.USER_ACTION,
        action: ACTIVITY_ACTIONS.PASSWORD_CHANGED,
        title: `Changed password`,
        description: `Updated account password for enhanced security`,
        resourceType: RESOURCE_TYPES.USER_ACCOUNT,
        resourceId: user?.id || "",
        resourceName: "Account Security",
        category: ACTIVITY_CATEGORIES.SECURITY,
        metadata: {
          security_action: "password_change",
          timestamp: new Date().toISOString(),
        },
      });

      toast.success("Password updated successfully.", { id: loadingToast });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      toast.error(`We couldn't update your password: ${err.message}`, {
        id: loadingToast,
      });
    } finally {
      setSavingPassword(false);
    }
  };

  const handleEnable2FA = (method: "authenticator" | "sms") => {
    toast.message(
      method === "authenticator"
        ? "Authenticator setup is coming soon."
        : "SMS verification is coming soon.",
      {
        description:
          "Two-factor sign-in will be available here shortly. We'll let you know the moment it's ready.",
      }
    );
  };

  const handleRevoke = (session: SecuritySession) => {
    if (session.current) return;
    setSessions((prev) => prev.filter((s) => s.id !== session.id));
    toast.success(`Signed out of ${session.device}.`);
  };

  const handleSignOutEverywhere = () => {
    setSessions((prev) => prev.filter((s) => s.current));
    toast.success("Signed out of every other device.");
  };

  return (
    <>
      <AcctPageHead
        icon="security"
        title="Security"
        subtitle="Keep your account locked down. Password, two-factor, and active sessions."
        tint={B.purple}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-5 items-start">
        {/* ── Left column — change password + two-factor ── */}
        <div className="flex flex-col gap-5">
          {/* change password */}
          <AcctCard label="Change password">
            <form onSubmit={handleUpdatePassword} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="hg-current-password" className={FIELD_LABEL}>
                  Current password
                </label>
                <input
                  id="hg-current-password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter your current password"
                  autoComplete="current-password"
                  className={INPUT_CLASS}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="hg-new-password" className={FIELD_LABEL}>
                  New password
                </label>
                <input
                  id="hg-new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter a new password"
                  autoComplete="new-password"
                  className={INPUT_CLASS}
                />
                <p className="text-[11.5px] text-gray-500">
                  At least 6 characters. Longer is stronger.
                </p>
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="hg-confirm-password" className={FIELD_LABEL}>
                  Confirm new password
                </label>
                <input
                  id="hg-confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your new password"
                  autoComplete="new-password"
                  className={INPUT_CLASS}
                />
              </div>

              <div className="mt-1">
                <button
                  type="submit"
                  disabled={savingPassword}
                  className="h-10 px-4 rounded-full border border-transparent inline-flex items-center justify-center gap-1.5 text-[13.5px] font-bold text-white bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-lg shadow-genie-primary/30 transition-all hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {savingPassword && <Loader2 className="w-4 h-4 animate-spin" />}
                  {savingPassword ? "Updating..." : "Update password"}
                </button>
              </div>
            </form>
          </AcctCard>

          {/* two-factor */}
          <AcctCard label="Two-factor authentication">
            <div className="flex items-center gap-3.5">
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${B.amber}1a` }}
              >
                <AlertTriangle className="w-[21px] h-[21px]" style={{ color: B.amber }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[15px] font-bold text-[#222B45]">Two-factor is off</p>
                <p className="text-[12.5px] text-[#64748B] mt-0.5 leading-relaxed">
                  Add a second step at sign-in so a password alone can't get in.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2.5 mt-[18px]">
              <AcctButton
                label="Set up authenticator app"
                kind="tinted"
                icon="shield"
                onClick={() => handleEnable2FA("authenticator")}
              />
              <AcctButton
                label="Use SMS"
                kind="ghost"
                onClick={() => handleEnable2FA("sms")}
              />
            </div>
          </AcctCard>
        </div>

        {/* ── Right column — active sessions ── */}
        <AcctCard label="Active sessions">
          <p className="text-[13px] text-[#64748B] -mt-1.5 mb-3 leading-relaxed">
            Where your account is signed in right now. Revoke anything you don't
            recognise.
          </p>

          <div className="flex flex-col">
            {sessions.map((s, i) => (
              <div
                key={s.id}
                className={`flex items-center gap-3 py-3.5 ${
                  i === 0 ? "" : "border-t border-genie-border/60"
                }`}
              >
                <div
                  className="w-9 h-9 rounded-[10px] flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${s.tint}1a` }}
                >
                  <Globe className="w-[17px] h-[17px]" style={{ color: s.tint }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13.5px] font-bold text-[#222B45] truncate">
                    {s.device}
                  </p>
                  <p className="text-[12px] text-gray-500 truncate">
                    {s.where} · {s.when}
                  </p>
                </div>
                {s.current ? (
                  <span className="h-6 px-2.5 rounded-full inline-flex items-center text-[11px] font-bold flex-shrink-0 bg-genie-success/15 text-genie-success">
                    This device
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleRevoke(s)}
                    className="h-7 px-3 rounded-full border border-genie-border inline-flex items-center text-[11.5px] font-bold text-genie-secondary hover:bg-genie-secondary/5 transition-colors flex-shrink-0"
                  >
                    Revoke
                  </button>
                )}
              </div>
            ))}
          </div>

          {sessions.some((s) => !s.current) && (
            <div className="mt-3 pt-3.5 border-t border-genie-border/60">
              <button
                type="button"
                onClick={handleSignOutEverywhere}
                className="h-10 px-4 rounded-full border border-genie-border inline-flex items-center justify-center gap-1.5 text-[13.5px] font-bold text-[#64748B] bg-white transition-all hover:opacity-90"
              >
                <RefreshCw className="w-[15px] h-[15px] text-genie-primary" />
                Sign out everywhere else
              </button>
            </div>
          )}
        </AcctCard>
      </div>
    </>
  );
};

export default SecurityTab;
